<template>
  <BasePaneCard
    heading="Funil de sucesso do cliente"
    headingTooltip="Dados de todos os leads baseados<br>em safra, período, ação ou origem."
    :isLoading="isLoading"
  >
    <BaseGrid
      v-if="!dataset?.error"
      columns="3"
    >
      <div class="acquisition-funnel grid-span-2">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="acquisition-funnel__step"
          :class="{
            'acquisition-funnel__step--off': step.every(info => info.isActive === false)
          }"
        >
          <div
            v-for="info in step"
            :key="info.key"
            class="acquisition-funnel__step__info"
            :class="{
              'acquisition-funnel__step__info--off': !info.isActive,
              'acquisition-funnel__step__info--disabled': checkPageFilterException(filterExceptions[info.key])
            }"
          >
            <div
              class="acquisition-funnel__step__info__label"
              v-tooltip="{
                theme: 'tooltip-right',
                content: info.tooltip
              }"
            >
              {{ info.label }}
            </div>

            <div
              class="acquisition-funnel__step__info__total"
              v-tooltip="{
                theme: 'tooltip-right',
                content: info.tooltip
              }"
            >
              {{ info.total | number }}
            </div>

            <div
              v-if="info.badge"
              class="acquisition-funnel__step__info__badge"
            >
              <span>
                {{ info.badge | percentage }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </BaseGrid>

    <BaseEmptyState v-else>
      <SvgIconRobot/>

      <h3>
        Ops! Algo deu errado...
      </h3>

      <p>
        Por favor tente novamente.
      </p>
    </BaseEmptyState>
  </BasePaneCard>
</template>

<script>
import { dateFilter, numberFilter, percentageFilter } from '@/utils/filters'
import { pageFilterExceptionMixin } from '@/utils/mixins'
import BaseGrid from '@/components/base/BaseGrid'
import BasePaneCard from '@/components/base/BasePaneCard'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'
import dayjs from 'dayjs'

export default {
  name: 'WidgetAcquisitionFunnel',

  components: {
    BaseGrid,
    BasePaneCard,
    BaseEmptyState
  },

  filters: {
    date: dateFilter,
    number: numberFilter,
    percentage: percentageFilter
  },

  mixins: [
    pageFilterExceptionMixin
  ],

  data () {
    return {
      isLoading: false,
      dataset: null,
      steps: [
        [
          {
            key: 'total_leads_valid',
            label: 'Entrada de leads',
            total: 0,
            badge: null,
            tooltip: 'Todos os leads disponíveis<br>e válidos para contato.',
            filterExceptions: ['csat', 'feeling'],
            isActive: true
          }
        ],
        [
          {
            key: 'total_leads_worked',
            label: 'Conversas iniciadas',
            total: 0,
            badge: 0,
            tooltip: 'Contatados e<br>aguardando resposta.',
            filterExceptions: ['csat', 'feeling'],
            isActive: true
          }
        ],
        [
          {
            key: 'total_leads_contacted',
            label: 'Conversas em andamento',
            total: 0,
            badge: 0,
            tooltip: 'Conversas que<br>obtiveram resposta.',
            filterExceptions: ['csat'],
            isActive: true
          },
        ],
        [
          {
            key: 'total_leads_finished',
            label: 'Conversas finalizadas',
            total: 0,
            badge: 0,
            tooltip: 'Finalizados e tabulados<br>como conversão e recusa.',
            filterExceptions: [],
            isActive: true
          },
          {
            key: 'total_conversions',
            label: 'Finalizados com sucesso',
            total: 0,
            badge: 0,
            tooltip: 'Conversas finalizadas com<br>status de <strong>conversão</strong>.',
            filterExceptions: [],
            isActive: true
          },
          {
            key: 'total_refusals',
            label: 'Finalizados com recusa',
            total: 0,
            badge: 0,
            tooltip: 'Conversas finalizadas com<br>status de <strong>recusa</strong>.',
            filterExceptions: [],
            isActive: true
          }
        ]
      ]
    }
  },

  computed: {
    ...mapState('base', [
      'mainApi',
      'pageFilter'
    ]),

    ...mapGetters('auth', [
      'userId',
      'platformId'
    ]),

    stepKeys () {
      return this.steps
        .flat()
        .map(step => step.key)
    },

    filterExceptions () {
      const exceptions = {}

      this.steps.flat().forEach(step => {
        exceptions[step.key] = step.filterExceptions
      })

      return exceptions
    }
  },

  watch: {
    pageFilter () {
      this.fetchData()
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    getFetchDataParams (filtersExceptions = []) {
      let params = ''
      let [dateStart, dateEnd] = this.pageFilter.dateRange
      const { action, csat, feeling } = this.pageFilter

      if (!dateStart || !dateEnd) {
        dateStart = dayjs()
        dateEnd = dayjs()
      }

      params += `/${dateFilter(dateStart, 'base')}/${dateFilter(dateEnd, 'base')}`
      params += '?showFunnel=true'

      if (action.length) {
        params += `&actionId=${action.join()}`
      }

      if (csat.length && !filtersExceptions.includes('csat')) {
        params += `&csat=${csat.join()}`
      }

      if (feeling.length && !filtersExceptions.includes('feeling')) {
        params += `&sentiments=${feeling.join()}`
      }

      return params
    },

    async fetchData () {
      this.isLoading = true

      try {
        const responses = this.stepKeys.map(stepKey => {
          const url = `${this.mainApi}lead/metrics/${stepKey}${this.getFetchDataParams(this.filterExceptions[stepKey])}`

          return axios.get(url)
        })

        await axios
          .all(responses)
          .then(axios.spread((
            { data: total_leads_valid },
            { data: total_leads_worked },
            { data: total_leads_contacted },
            { data: total_leads_finished },
            { data: total_conversions },
            { data: total_refusal }
          ) => {
            this.dataset = [
              total_leads_valid,
              total_leads_worked,
              total_leads_contacted,
              total_leads_finished,
              total_conversions,
              total_refusal
            ]

            this.updateSteps()
          }))
      } catch (error) {
        this.dataset = { error }

        throw error
      } finally {
        this.isLoading = false
      }
    },

    updateSteps () {
      let sourcePrevious = null

      this.steps.forEach(step => {
        step.forEach((info, infoIndex) => {
          const source = this.dataset.find(item => item.name === info.key)

          info.total = source.value | 0

          if (sourcePrevious) {
            info.badge = source.value >= 1
              ? Math.round((source.value / sourcePrevious.value) * 100)
              : 0
          }

          if (!infoIndex) {
            sourcePrevious = source
          }
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.acquisition-funnel {
  padding: 20px 20px 20px 0;

  &__step {
    position: relative;
    padding: 15px;

    &::before {
      content: '';
      z-index: 0;
      position: absolute;
      top: 0;
      right: 0;
      display: block;
      height: 100%;
      width: 100%;
      border: 2px solid nth($colors-custom, 4);
      border: {
        top: 0;
        left: 0;
      }
      border-radius: 0 0 8px 0;
      transform: skew(-15deg, 0);
      transform-origin: bottom left;
    }

    &--off {
      display: none;
    }

    @for $i from 0 through 4 {
      &:nth-child(#{$i + 1}) {
        width: percentage(1 - ($i * 0.11));
      }
    }

    &__info {
      z-index: 1;
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 0;
      margin-right: 30px;

      &:nth-child(n + 3) {
        border-top: 1px solid nth($colors-custom, 4);
      }

      &:not(:first-child) & {
        &__label {
          color: nth($colors-custom, 2);
          font-weight: 600;
        }

        &__total {
          color: nth($colors-custom, 2);
          font: {
            weight: 600;
            size: 12px;
          }
        }

        &__badge {
          color: nth($colors-custom, 2);
          border-color: nth($colors-custom, 2);
        }
      }

      @for $i from 1 through 2 {
        $positions: (
          1: 42px,
          2: 32px
        );

        &:not(:last-child):nth-child(#{$i}) & {
          &__badge {
            transform: translateX(calc(100% + (#{map-get($positions, $i)})));
          }
        }
      }

      &--off {
        display: none;
      }

      &--disabled & {
        &__total {
          opacity: 0.3;
        }

        &__badge {
          border-color: nth($colors-custom, 4) !important;

          span {
            opacity: 0.3;
          }
        }
      }

      &__label {
        font: {
          weight: 400;
          size: 14px;
        }
        line-height: 100%;
      }

      &__total {
        font: {
          weight: 700;
          size: 20px;
        }
        line-height: 100%;
      }

      &__badge {
        position: absolute;
        right: 0;
        width: 60px;
        padding: 5px 0;
        border: 1px solid nth($colors-custom, 1);
        border-radius: 2em;
        transform: translateX(calc(100% + 25px));
        background: #fff;
        font: {
          weight: 600;
          size: 12px;
        }
        line-height: 100%;
        text-align: center;
      }
    }
  }
}
</style>
